import { useState } from "react";
import { camelCase } from "lodash";
import { useGetAccessToken } from "auth/utils/oktaAuth";

export enum UploadFileCategory {
  TaskAttachment = "hospitality_task_attachment",
  TaskCommentAttachment = "hospitality_task_comment_attachment",
  UserAvatar = "hospitality_user_avatar",
}

export interface UploadFileResponse {
  format: string;
  publicId: string;
  originalFile: File;
  resourceType: string;
  url: string;
}

export interface UploadErrorResponse {
  error: Error | null;
  originalFile: File;
}

export interface UploadFilesReducer {
  errors: Error[];
  erroredFiles: UploadErrorResponse[];
  uploadedFiles: UploadFileResponse[];
}
export interface UploadFilesPayload extends UploadFilesReducer {
  success: boolean;
}

export interface UploadFileInput {
  authToken?: string | null;
  category: UploadFileCategory;
  file: File;
}

export interface UploadFilesInput {
  category: UploadFileCategory;
  files: File[];
}

export interface UploadFilesState {
  data: UploadFilesPayload | null;
  error: Error | null;
  loading: boolean;
}

export const UPLOAD_ENDPOINT = `${ADMIN_HOST}/tom/api/upload`;

// TODO: Move to Util? Support deep conversion? Blah.
// This shallowly converts `camel_case` to `camelCase` on the resulting JSON response keys.
interface ShallowObject {
  [key: string]: any;
}
const convertShallowObjectKeysToCamelCase = (obj: ShallowObject) =>
  Object.keys(obj).reduce(
    (memo, key) => ({
      ...memo,
      [camelCase(key)]: obj[key],
    }),
    {},
  );

// TODO: Move to Util? This is super uploadFile specific tho.
// This just wraps multiple JSON responses into a common / GQL like format.
interface UploadFileResponseWrapper {
  file: UploadFileResponse | null;
  originalFile: File;
  error?: Error | null;
}

const convertToUploadFileResponse = (results: UploadFileResponseWrapper[]) => {
  const initialReducerState: UploadFilesReducer = {
    erroredFiles: [],
    uploadedFiles: [],
    errors: [],
  };
  return results.reduce((acc, memo) => {
    return {
      ...acc,
      uploadedFiles: memo.file
        ? [
            ...acc.uploadedFiles,
            { ...memo.file, originalFile: memo.originalFile },
          ]
        : acc.uploadedFiles,
      erroredFiles: memo.error
        ? [
            ...acc.erroredFiles,
            { error: memo.error, originalFile: memo.originalFile },
          ]
        : acc.erroredFiles,
      errors: memo.error ? [...acc.errors, memo.error] : acc.errors,
    };
  }, initialReducerState);
};

export const onUploadFile = ({
  authToken,
  category,
  file,
}: UploadFileInput) => {
  const formData = new FormData();
  formData.append("category", category);
  formData.append("file", file);

  return fetch(UPLOAD_ENDPOINT, {
    method: "POST",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    body: formData,
  })
    .then(async result => {
      const uploadedFile = await result.json();
      return {
        file: convertShallowObjectKeysToCamelCase(
          uploadedFile,
        ) as UploadFileResponse,
        originalFile: file,
        error: null,
      };
    })
    .catch(err => ({ file: null, originalFile: file, error: err }));
};

export const useUploadFiles = () => {
  const authToken = useGetAccessToken();
  const [uploadState, setUploadState] = useState<UploadFilesState>({
    data: null,
    error: null,
    loading: false,
  });
  const onUploadFiles = ({ category, files }: UploadFilesInput) => {
    setUploadState({
      data: uploadState.data,
      error: null,
      loading: true,
    });
    return Promise.all(
      files.map(file => onUploadFile({ authToken, file, category })),
    )
      .then(results => {
        const { erroredFiles, uploadedFiles, errors } =
          convertToUploadFileResponse(results);
        const newUploadState = {
          error: errors.length
            ? new Error(
                errors.map(err => err.message || "Unexpected Error").join(", "),
              )
            : null,
          data: {
            errors,
            success: uploadedFiles.length > 0,
            erroredFiles,
            uploadedFiles,
          },
          loading: false,
        };
        setUploadState(newUploadState);
        return newUploadState;
      })
      .catch(err => {
        const newUploadState = {
          error: err,
          data: null,
          loading: false,
        };
        setUploadState(newUploadState);
        return newUploadState;
      });
  };

  return { onUploadFiles, uploadState };
};
